import { ArrowUpRight, Instagram, Facebook, Linkedin, MapPin } from "lucide-react";
import { PORTAL_URL, LOGO_URL, SOCIALS, NAV_LINKS, LOCATIONS } from "@/lib/site-data";

const WhatsApp = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18" {...props}>
    <path d="M12.04 2c-5.46 0-9.9 4.44-9.9 9.9 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22c5.46 0 9.9-4.44 9.9-9.9S17.5 2 12.04 2Zm0 18.06c-1.5 0-2.97-.4-4.25-1.16l-.3-.18-3.12.82.83-3.04-.2-.31a8.16 8.16 0 0 1-1.26-4.33c0-4.52 3.68-8.2 8.2-8.2s8.2 3.68 8.2 8.2-3.68 8.2-8.2 8.2Zm4.5-6.15c-.25-.12-1.47-.72-1.7-.81-.23-.08-.4-.12-.56.13-.17.25-.64.8-.79.97-.14.17-.29.19-.54.06-.25-.12-1.05-.39-1.99-1.23-.74-.66-1.23-1.47-1.38-1.72-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.14.16-.25.25-.41.08-.17.04-.31-.02-.43-.06-.12-.56-1.34-.76-1.84-.2-.48-.4-.42-.56-.43h-.48c-.17 0-.43.06-.66.31-.23.25-.86.85-.86 2.07 0 1.22.89 2.4 1.01 2.57.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.07.14-1.18-.06-.11-.22-.17-.47-.29Z" />
  </svg>
);
const SOCIAL_ICONS = { WhatsApp, Instagram, Facebook, LinkedIn: Linkedin };

const COLS = [
  { title: "Services", links: ["Air Cargo", "Sea Cargo", "Door To Door", "Local Deliveries", "Warehousing"] },
  { title: "Company", links: ["About", "Business Solutions", "Reviews", "Careers"] },
  { title: "Support", links: ["FAQ", "Track Shipment", "Contact", "Rate Calculator"] },
];

export default function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="relative bg-[#1F1F1F] pt-20 pb-10">
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <div className="grid gap-10 border-b border-white/10 pb-14 md:grid-cols-[1.6fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-3">
              <div className="rounded-2xl bg-white p-2.5">
                <img src={LOGO_URL} alt="Xpress N'Go Ship Ltd logo" className="h-16 w-auto object-contain" />
              </div>
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/60">
              Fast global shipping. Courier, freight, fulfilment and customs — you order, we deliver.
            </p>
            <div className="mt-4 space-y-1 text-sm text-white/70">
              <div>876 825 6468 · 876 538 6101 · 876 826 1828</div>
              <div><a href="https://www.xpressngoship.com" className="hover:text-white">www.xpressngoship.com</a></div>
              <div><a href="mailto:xpressngoship@gmail.com" className="hover:text-white">xpressngoship@gmail.com</a></div>
            </div>
            <a href={PORTAL_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-portal"
              className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-[#1F1F1F] hover:bg-white/90">
              Client portal <ArrowUpRight size={15} />
            </a>
            <div className="mt-6 flex items-center gap-3">
              {SOCIALS.map((s) => {
                const Icon = SOCIAL_ICONS[s.label];
                return (
                  <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                    data-testid={`social-${s.label.toLowerCase()}`}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 hover:border-[#E31B23] hover:text-white hover:bg-[#E31B23]/20">
                    {Icon ? <Icon size={18} /> : null}
                  </a>
                );
              })}
            </div>
          </div>

          {COLS.map((c) => (
            <div key={c.title}>
              <div className="mb-4 text-xs uppercase tracking-wider text-white/40">{c.title}</div>
              <ul className="space-y-3">
                {c.links.map((l) => (
                  <li key={l}><a href="#" className="text-sm text-white/70 hover:text-white">{l}</a></li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="grid gap-6 border-b border-white/10 py-8 sm:grid-cols-2">
          {LOCATIONS.map((loc, i) => (
            <div key={i} className="flex items-start gap-3" data-testid={`footer-location-${i}`}>
              <MapPin size={18} className="mt-0.5 flex-shrink-0 text-[#E31B23]" />
              <div>
                <div className="text-sm font-semibold text-white">{loc.name}</div>
                <div className="text-sm text-white/60">{loc.address}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="py-10">
          <div className="font-display font-extrabold leading-none text-white/[0.05] text-outline text-[16vw] tracking-tighter select-none">
            XPRESSNGOSHIP
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-4 text-xs text-white/50 sm:flex-row">
          <span>© {year} XPRESSNGOSHIP LTD. All rights reserved.</span>
          <span className="flex gap-6">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Cookies</a>
          </span>
        </div>
      </div>
    </footer>
  );
}
